import { useState } from "react";
import { FieldRenderer } from "@/features/inventory/item-detail/components/FieldRenderer";
import { RowControls } from "./RowControls";
import { ServerFetchError } from "./ServerFetchError";
import { validateLinkField } from "./utils/linkValidation";
import type { ChildTableRowProps, RowValidationError } from "./types";
import { cn } from "@/lib/utils";
import type { DocFieldSchema } from "@/lib/erp/meta";

/**
 * Renders a single row with all its fields.
 * Supports server fetch loading shimmer and inline error display.
 */
export function ChildTableRow({
  row,
  rowIndex,
  fields,
  onRowChange,
  onEdit,
  onDelete,
  onDuplicate,
  disabled,
  errors = [],
  fetchState,
  triggerFields,
}: ChildTableRowProps) {
  // State for link validation errors (from blur)
  const [linkErrors, setLinkErrors] = useState<RowValidationError[]>([]);

  if (fields.length === 0) {
    return null;
  }

  // Set of trigger field names for quick lookup (shimmer exclusion)
  const triggerFieldSet = triggerFields ? new Set(triggerFields) : undefined;

  // Get error for a specific field (combines required errors with link errors)
  const getFieldError = (fieldname: string) => {
    return [...errors, ...linkErrors].find((err) => err.fieldname === fieldname);
  };

  // Validate link on blur (single cell)
  const handleLinkBlur = async (field: DocFieldSchema, value: any) => {
    // Only validate Link fields
    if (field.fieldtype !== "Link" || !field.options || !value) {
      return;
    }

    const validation = await validateLinkField(field.options, value);

    if (!validation.valid) {
      setLinkErrors((prev) => [
        ...prev.filter((e) => e.fieldname !== field.fieldname),
        {
          rowIndex,
          fieldname: field.fieldname,
          fieldLabel: field.label,
          message: validation.error || "Invalid link",
        },
      ]);
    } else {
      // Clear error if validation passes
      setLinkErrors((prev) => prev.filter((e) => e.fieldname !== field.fieldname));
    }
  };

  // Combine required errors with link errors for display
  const allErrors = [...errors, ...linkErrors];

  // Should this field show shimmer? Only non-trigger fields when loading
  const shouldShimmer = (fieldname: string) => {
    if (!fetchState?.loading) return false;
    if (triggerFieldSet?.has(fieldname)) return false;
    return true;
  };

  return (
    <div
      className={cn(
        "grid gap-4 px-4 py-3 hover:bg-muted/30 transition-colors border-b last:border-b-0",
        allErrors.length > 0 && "bg-destructive/5 border-destructive/20"
      )}
      data-fetching={fetchState?.loading ? "true" : undefined}
      style={{
        gridTemplateColumns: `repeat(${fields.length}, minmax(0, 1fr)) auto`,
      }}
    >
      {fields.map((field) => {
        const fieldError = getFieldError(field.fieldname);

        return (
          <div
            key={field.fieldname}
            className={cn(
              "space-y-1",
              shouldShimmer(field.fieldname) && "animate-pulse bg-muted/30 rounded-md"
            )}
          >
          <FieldRenderer
            field={field}
            value={row[field.fieldname]}
            onChange={(value) => onRowChange({ [field.fieldname]: value })}
              onBlur={(value) => handleLinkBlur(field, value)}
            disabled={disabled || field.read_only === 1}
          />
            {fieldError && (
              <p className="text-xs text-destructive">
                {fieldError.message}
              </p>
            )}
        </div>
        );
      })}
      <div className="flex items-center justify-center">
        <RowControls
          onEdit={onEdit}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
          disabled={disabled}
        />
      </div>
      {fetchState?.error && <ServerFetchError />}
    </div>
  );
}
